import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useToast } from '@/hooks/useToast';
import { useConsumption } from '@/hooks/useConsumption';
import { useLocations } from '@/hooks/useLocations';
import {
  Package,
  Search,
  Plus,
  TrendingUp,
  TrendingDown,
  Calendar,
  DollarSign,
  Filter,
  Download,
} from 'lucide-react';

export default function ConsumptionPage() {
  const { toast } = useToast();
  const {
    getConsumptionRecords,
    getDepartmentSummary,
    getDailyStats,
    createConsumption,
  } = useConsumption();
  const { data: locations } = useLocations();

  const [selectedLocation, setSelectedLocation] = useState<string>('');
  const [selectedDepartment, setSelectedDepartment] = useState<string>('');
  const [dateRange, setDateRange] = useState<'7' | '30' | '90'>('30');

  const { data: records = [], isLoading } = getConsumptionRecords({
    location_id: selectedLocation || undefined,
    start_date: new Date(Date.now() - parseInt(dateRange) * 24 * 60 * 60 * 1000).toISOString(),
  });

  const { data: summary } = getDepartmentSummary({
    start_date: new Date(Date.now() - parseInt(dateRange) * 24 * 60 * 60 * 1000).toISOString(),
  });

  const { data: dailyStats = [] } = getDailyStats(parseInt(dateRange));

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      maximumFractionDigits: 0,
    }).format(value);
  };

  const handleRecordConsumption = () => {
    toast({
      title: 'Record Consumption',
      description: 'Opening consumption form...',
    });
  };

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Department Consumption</h1>
          <p className="text-muted-foreground">
            Track item consumption by department and patient services
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline">
            <Download className="h-4 w-4 mr-2" />
            Export Report
          </Button>
          <Button onClick={handleRecordConsumption}>
            <Plus className="h-4 w-4 mr-2" />
            Record Consumption
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Total Records</CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{records.length}</div>
            <p className="text-xs text-muted-foreground">
              Last {dateRange} days
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Total Quantity</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {summary?.total_consumption.toLocaleString() || 0}
            </div>
            <p className="text-xs text-muted-foreground">Items consumed</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Total Value</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrency(summary?.total_value || 0)}
            </div>
            <p className="text-xs text-muted-foreground">Consumption cost</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Departments</CardTitle>
            <Filter className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {summary?.departments.length || 0}
            </div>
            <p className="text-xs text-muted-foreground">Active departments</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex gap-4 items-center">
        <Select value={dateRange} onValueChange={(v: '7' | '30' | '90') => setDateRange(v)}>
          <SelectTrigger className="w-[180px]">
            <Calendar className="h-4 w-4 mr-2" />
            <SelectValue placeholder="Date range" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Last 7 days</SelectItem>
            <SelectItem value="30">Last 30 days</SelectItem>
            <SelectItem value="90">Last 90 days</SelectItem>
          </SelectContent>
        </Select>

        <Select value={selectedLocation} onValueChange={setSelectedLocation}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="All Locations" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="">All Locations</SelectItem>
            {locations?.map((loc) => (
              <SelectItem key={loc.location_id} value={loc.location_id}>
                {loc.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search items..." className="pl-10" />
        </div>
      </div>

      {/* Department Summary */}
      <Card>
        <CardHeader>
          <CardTitle>Consumption by Department</CardTitle>
          <CardDescription>Summary of consumption by department</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Department</TableHead>
                <TableHead className="text-right">Records</TableHead>
                <TableHead className="text-right">Total Quantity</TableHead>
                <TableHead className="text-right">Total Value</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {summary?.departments.map((dept) => (
                <TableRow key={dept.department_id}>
                  <TableCell className="font-medium">{dept.department_name}</TableCell>
                  <TableCell className="text-right">{dept.total_items}</TableCell>
                  <TableCell className="text-right">{dept.total_quantity.toLocaleString()}</TableCell>
                  <TableCell className="text-right">{formatCurrency(dept.total_value)}</TableCell>
                </TableRow>
              ))}
              {(!summary?.departments || summary.departments.length === 0) && (
                <TableRow>
                  <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                    No consumption data for this period
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Recent Records */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Consumption Records</CardTitle>
          <CardDescription>Latest consumption entries</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Item</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>Department</TableHead>
                <TableHead className="text-right">Quantity</TableHead>
                <TableHead>Patient/Service</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : records.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                    No consumption records found
                  </TableCell>
                </TableRow>
              ) : (
                records.slice(0, 20).map((record) => (
                  <TableRow key={record.consumption_id}>
                    <TableCell>
                      {new Date(record.consumed_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <div>
                        <div className="font-medium">{record.item_name}</div>
                        <div className="text-xs text-muted-foreground">
                          {record.item_code}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>{record.location_name}</TableCell>
                    <TableCell>{record.department_name}</TableCell>
                    <TableCell className="text-right font-medium">
                      {record.quantity.toLocaleString()}
                    </TableCell>
                    <TableCell>
                      {record.patient_id ? (
                        <Badge variant="outline">Patient: {record.patient_id}</Badge>
                      ) : record.service_id ? (
                        <Badge variant="outline">Service: {record.service_id}</Badge>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
